import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { FlashlightIcon } from 'lucide-react';
export function Hero() {
  // Generate random dust particles
  const particles = Array.from({
    length: 20
  }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: 10 + Math.random() * 20,
    delay: Math.random() * 5
  }));
  return <>
      <section className="relative min-h-screen flex items-start justify-center overflow-hidden bg-[#0a0a0a] pt-24 sm:pt-28" data-id="element-168">
        {/* Cinematic Background */}
        <div className="absolute inset-0 z-0" data-id="element-169">
          <motion.img initial={{
          scale: 1.05
        }} animate={{
          scale: 1
        }} transition={{
          duration: 10,
          ease: 'easeOut'
        }} src="https://images.unsplash.com/photo-1505682634904-d7c8d95cdc50?q=80&w=2000&auto=format&fit=crop" alt="Dark typewriter desk" className="w-full h-full object-cover opacity-20" data-id="element-170" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/90 via-[#0a0a0a]/80 to-[#0a0a0a]" data-id="element-171" />

          {/* Animated Flashlight Beam */}
          <motion.div animate={{
          rotate: [-5, 5, -5],
          opacity: [0.3, 0.5, 0.3]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute -top-[20%] left-1/2 -translate-x-1/2 w-[150%] h-[150%] bg-[conic-gradient(from_180deg_at_50%_0%,transparent_130deg,rgba(236,227,208,0.15)_180deg,transparent_230deg)] origin-top blur-2xl" data-id="element-172" />
        </div>

        {/* Dust Particles */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none" data-id="element-173">
          {particles.map(p => <motion.div key={p.id} animate={{
          y: ['0vh', '-100vh'],
          x: [`${p.x}vw`, `${p.x + (Math.random() * 10 - 5)}vw`],
          opacity: [0, Math.random() * 0.5 + 0.2, 0]
        }} transition={{
          duration: p.duration,
          delay: p.delay,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute bottom-0 w-1 h-1 bg-[#ece3d0] rounded-full blur-[1px]" style={{
          left: `${p.x}vw`
        }} data-id="element-174" />)}
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-175">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          ease: [0.16, 1, 0.3, 1]
        }} className="space-y-5 sm:space-y-8" data-id="element-176">
            <div className="inline-flex items-center space-x-2 text-[#8b1a1a] mb-4 bg-[#8b1a1a]/10 px-4 py-1.5 rounded-full border border-[#8b1a1a]/20 backdrop-blur-sm shadow-[0_0_15px_rgba(139,26,26,0.2)]" data-id="element-177">
              <FlashlightIcon className="w-4 h-4" data-id="element-178" />
              <span className="font-typewriter text-sm tracking-wider uppercase" data-id="element-179">
                Case Files Open
              </span>
            </div>

            <h1 className="font-typewriter sm:text-5xl lg:text-7xl text-[#ece3d0] drop-shadow-2xl text-[32px]" data-id="element-180">
              You've watched every documentary.
              <br data-id="element-181" />
              Every podcast. Every episode.
              <br data-id="element-182" />
              <span className="text-[#8b1a1a] drop-shadow-[0_0_10px_rgba(139,26,26,0.5)]" data-id="element-183">
                But you've never been the detective.
              </span>
            </h1>

            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4,
            duration: 1
          }} className="font-serif-body text-xl sm:text-3xl text-[#ece3d0]/90 italic max-w-3xl mx-auto" data-id="element-184">
              Tonight, that changes.
            </motion.p>

            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.6,
            duration: 1
          }} className="font-serif-body text-base sm:text-xl text-[#ece3d0]/70 max-w-2xl mx-auto leading-relaxed" data-id="element-185">
              Interactive mystery experiences based on real unsolved cases in
              the United States. Downloadable. Printable. Ready to play in
              minutes.
            </motion.p>

            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.8,
            duration: 1
          }} className="text-sm text-[#ece3d0]/50 font-typewriter tracking-widest uppercase" data-id="element-186">
              No apps. No subscriptions. No screens.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1,
            duration: 0.8,
            ease: [0.16, 1, 0.3, 1]
          }} className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4 sm:gap-4 sm:pt-8" data-id="element-187">
              <button className="bg-[#8b1a1a] hover:bg-[#a01f1f] text-[#ece3d0] px-6 py-3 sm:px-8 sm:py-4 rounded font-typewriter tracking-wider transition-all hover:scale-105 w-full sm:w-auto text-sm sm:text-lg shadow-[0_0_20px_rgba(139,26,26,0.4)] border border-[#a01f1f]" data-id="element-188">
                GET THE COMPLETE COLLECTION — $42
              </button>
              <button className="border border-[#ece3d0]/30 hover:border-[#ece3d0]/60 text-[#ece3d0] px-6 py-3 sm:px-8 sm:py-4 rounded font-typewriter tracking-wider transition-all hover:scale-105 w-full sm:w-auto text-sm sm:text-lg backdrop-blur-sm bg-[#0a0a0a]/50 hover:bg-[#0a0a0a]/80" data-id="element-189">
                START WITH ONE CASE — $17
              </button>
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/80 to-transparent z-[5] pointer-events-none" data-id="element-190" />
      </section>
    </>;
}